import React, { createContext, useContext, ReactNode } from 'react';

// Permission types
export type Permission = string;
export type Role = string;

// User context interface
export interface UserContext {
  user: {
    id: string;
    email: string;
    role?: string;
    permissions?: Permission[];
    businessId?: string;
    branchId?: string;
  } | null;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
}

// Create context
const PermissionContext = createContext<UserContext | undefined>(undefined);

// Permission provider component
export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get user data from localStorage (auth-storage)
  const getUserData = () => {
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (!authStorage) return null;
      
      const authData = JSON.parse(authStorage);
      return authData.state?.user || null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  };

  const user = getUserData();

  // Check if user has specific permission
  const hasPermission = (permission: Permission): boolean => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions: Permission[]): boolean => {
    if (!user?.permissions) return false;
    return permissions.some(permission => user.permissions!.includes(permission));
  };

  // Check if user has specific role
  const hasRole = (role: Role): boolean => {
    if (!user?.role) return false;
    return user.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: Role[]): boolean => {
    if (!user?.role) return false;
    return roles.includes(user.role);
  };

  const contextValue: UserContext = {
    user,
    hasPermission,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// Hook to use permissions
export const usePermissions = (): UserContext => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// Permission guard component
export const PermissionGuard: React.FC<{
  permission?: Permission;
  permissions?: Permission[];
  role?: Role;
  roles?: Role[];
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ 
  permission, 
  permissions, 
  role, 
  roles, 
  children, 
  fallback = null 
}) => {
  const { hasPermission, hasAnyPermission, hasRole, hasAnyRole } = usePermissions();

  // Check permission
  if (permission && !hasPermission(permission)) {
    return <>{fallback}</>;
  }

  // Check any permissions
  if (permissions && !hasAnyPermission(permissions)) {
    return <>{fallback}</>;
  }

  // Check role
  if (role && !hasRole(role)) {
    return <>{fallback}</>;
  }

  // Check any roles
  if (roles && !hasAnyRole(roles)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

// Higher-order component for permission checking
export const withPermission = <P extends object>(
  Component: React.ComponentType<P>,
  options: {
    permission?: Permission;
    permissions?: Permission[];
    role?: Role;
    roles?: Role[];
    fallback?: ReactNode;
  }
) => {
  return (props: P) => (
    <PermissionGuard {...options}>
      <Component {...props} />
    </PermissionGuard>
  );
};

// Permission constants for easy reference
export const PERMISSIONS = {
  // Smart Ledger
  LEDGER_VIEW: 'ledger_view',
  LEDGER_CREATE: 'ledger_create',
  LEDGER_EDIT: 'ledger_edit',
  LEDGER_DELETE: 'ledger_delete',
  LEDGER_APPROVE: 'ledger_approve',
  LEDGER_REPORTS: 'ledger_reports',

  // Expense Management
  EXPENSE_VIEW: 'expense_view',
  EXPENSE_CREATE: 'expense_create',
  EXPENSE_EDIT: 'expense_edit',
  EXPENSE_DELETE: 'expense_delete',
  EXPENSE_APPROVE: 'expense_approve',
  EXPENSE_PAY: 'expense_pay',

  // Document Vault
  DOCUMENT_VIEW: 'document_view',
  DOCUMENT_CREATE: 'document_create',
  DOCUMENT_EDIT: 'document_edit',
  DOCUMENT_DELETE: 'document_delete',
  DOCUMENT_DOWNLOAD: 'document_download',
  DOCUMENT_APPROVE: 'document_approve',

  // POS/Sales
  POS_VIEW: 'pos_view',
  POS_SELL: 'pos_sell',
  POS_REFUND: 'pos_refund',
  POS_VOID: 'pos_void',

  // Dashboard
  DASHBOARD_VIEW: 'dashboard_view',
  DASHBOARD_ANALYTICS: 'dashboard_analytics',

  // Business Profile
  PROFILE_VIEW: 'profile_view',
  PROFILE_EDIT: 'profile_edit',

  // User Management
  USER_VIEW: 'user_view',
  USER_CREATE: 'user_create',
  USER_EDIT: 'user_edit',
  USER_DELETE: 'user_delete',
  USER_ASSIGN_ROLES: 'user_assign_roles',

  // System
  SYSTEM_CONFIG: 'system_config',
  SYSTEM_BACKUP: 'system_backup',
  SYSTEM_AUDIT: 'system_audit',
} as const;

// Role constants
export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  BUSINESS_OWNER: 'business_owner',
  ACCOUNTANT: 'accountant',
  AUDITOR: 'auditor',
  OPERATIONS_MANAGER: 'operations_manager',
  CASHIER: 'cashier',
  COMPLIANCE_OFFICER: 'compliance_officer',
} as const;

// Permission groups for common checks
export const PERMISSION_GROUPS = {
  // Full ledger access
  LEDGER_FULL: [
    PERMISSIONS.LEDGER_VIEW,
    PERMISSIONS.LEDGER_CREATE,
    PERMISSIONS.LEDGER_EDIT,
    PERMISSIONS.LEDGER_DELETE,
    PERMISSIONS.LEDGER_APPROVE,
    PERMISSIONS.LEDGER_REPORTS,
  ],

  // Read-only ledger access
  LEDGER_READ_ONLY: [
    PERMISSIONS.LEDGER_VIEW,
    PERMISSIONS.LEDGER_REPORTS,
  ],

  // Full expense management
  EXPENSE_FULL: [
    PERMISSIONS.EXPENSE_VIEW,
    PERMISSIONS.EXPENSE_CREATE,
    PERMISSIONS.EXPENSE_EDIT,
    PERMISSIONS.EXPENSE_DELETE,
    PERMISSIONS.EXPENSE_APPROVE,
    PERMISSIONS.EXPENSE_PAY,
  ],

  // Expense approval only
  EXPENSE_APPROVAL: [
    PERMISSIONS.EXPENSE_VIEW,
    PERMISSIONS.EXPENSE_APPROVE,
  ],

  // Full document management
  DOCUMENT_FULL: [
    PERMISSIONS.DOCUMENT_VIEW,
    PERMISSIONS.DOCUMENT_CREATE,
    PERMISSIONS.DOCUMENT_EDIT,
    PERMISSIONS.DOCUMENT_DELETE,
    PERMISSIONS.DOCUMENT_DOWNLOAD,
    PERMISSIONS.DOCUMENT_APPROVE,
  ],

  // Document view only
  DOCUMENT_READ_ONLY: [
    PERMISSIONS.DOCUMENT_VIEW,
    PERMISSIONS.DOCUMENT_DOWNLOAD,
  ],

  // User management
  USER_MANAGEMENT: [
    PERMISSIONS.USER_VIEW,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_EDIT,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.USER_ASSIGN_ROLES,
  ],

  // System administration
  SYSTEM_ADMIN: [
    PERMISSIONS.SYSTEM_CONFIG,
    PERMISSIONS.SYSTEM_BACKUP,
    PERMISSIONS.SYSTEM_AUDIT,
  ],
} as const;
